import React, { useState, useCallback } from 'react'
import {
  ImageBackground,
  StyleSheet,
  View,
  ScrollView,
  Pressable,
  TextInput,
  Alert,
  KeyboardAvoidingView,
  Platform
} from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { useRouter } from 'expo-router'
import { useQuery } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Doc, Id } from '@ppwr/convex/_generated/dataModel'
import { useConvexScheduledTournament, type TableConfigParam } from '@/hooks/use-convex-scheduled-tournament'
import { DS } from '@/design-system'
import { AntDesign, MaterialIcons } from '@expo/vector-icons'
import { Button } from 'react-native-paper'
import { debounce } from 'lodash'
import { scheduleDayOfWeekUTC, scheduleTimeOfDayUTC, nextOccurrenceFromDayTime } from '../../utils'
import image from '../../../assets/Admin/pokerpower-watermark.png'

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const DIFFICULTY_OPTIONS = ['BEGINNER', 'GENERAL', 'ADVANCED'] as const

// getPublicDailySchedules dual-reads the recurring table (community rows) and not-yet-migrated legacy rows.
type ScheduleDoc = Doc<'schedulesRecurring'> | Doc<'gameSchedules'>

type TableConfigForm = TableConfigParam & { _key?: number }
let _nextConfigKey = 0
const nextKey = () => ++_nextConfigKey

const defaultTableConfig = (): TableConfigForm => ({
  name: 'Main',
  difficulty: 'GENERAL',
  numberOfTables: 1,
  blindTimer: 2,
  startingChips: 10000,
  turnDuration: 30,
  bigBlind: 200,
  smallBlind: 100,
  maxPlayers: 9,
  minPlayers: 2,
  _key: nextKey()
})

const styles = StyleSheet.create({
  screen: {
    flexGrow: 1
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8
  },
  titleRow: {
    flexDirection: 'row',
    gap: 8,
    alignItems: 'center'
  },
  content: {
    padding: 16,
    gap: 16
  },
  card: {
    backgroundColor: '#F6F2E9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E0E0E0'
  },
  cardActions: {
    flexDirection: 'row',
    gap: 8,
    marginTop: 12,
    flexWrap: 'wrap'
  },
  form: {
    backgroundColor: '#F6F2E9',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    gap: 12
  },
  formSection: {
    gap: 8,
    marginTop: 8
  },
  formSectionTitle: {
    marginBottom: 4
  },
  input: {
    height: 40,
    borderWidth: 1,
    borderColor: '#121212',
    borderRadius: 5,
    paddingHorizontal: 10,
    backgroundColor: 'white'
  },
  tableConfigBlock: {
    backgroundColor: 'rgba(255,255,255,0.7)',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#ddd'
  },
  tableConfigHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8
  }
})

function ScheduleCard({
  schedule,
  onEdit,
  onDelete
}: {
  schedule: ScheduleDoc
  onEdit: () => void
  onDelete: () => void
}) {
  const dayOfWeek = scheduleDayOfWeekUTC(schedule)
  const dayName = dayOfWeek != null ? DAY_NAMES[dayOfWeek] : '?'
  const { hours, minutes } = scheduleTimeOfDayUTC(schedule)
  const timeStr = `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')} UTC`
  const configs = schedule.tableConfigs ?? []

  return (
    <View style={styles.card}>
      <DS.Text variant="Heading 4/Medium">{schedule.name}</DS.Text>
      <DS.Text variant="Body Small/Regular">
        {dayName} at {timeStr}
      </DS.Text>
      {configs.length > 0 ? (
        <View style={styles.formSection}>
          <DS.Text variant="Caption/Regular" style={styles.formSectionTitle}>
            Table configs:
          </DS.Text>
          {configs.map((tc, i) => (
            <DS.Text key={`${tc.name}-${tc.difficulty}-${i}`} variant="Caption/Regular">
              • {tc.name} ({tc.difficulty}) — {tc.numberOfTables} table(s), blinds {tc.bigBlind != null && tc.smallBlind != null ? `${tc.smallBlind}/${tc.bigBlind}` : '—'}, {tc.startingChips} chips, {tc.turnDuration}s turn
            </DS.Text>
          ))}
        </View>
      ) : (
        <DS.Text variant="Caption/Regular">
          Difficulty: {('difficulty' in schedule ? schedule.difficulty : undefined) ?? '—'} | Tables:{' '}
          {('numberOfTables' in schedule ? schedule.numberOfTables : undefined) ?? 1}
        </DS.Text>
      )}
      <View style={styles.cardActions}>
        <Button compact mode="outlined" onPress={onEdit}>
          Edit
        </Button>
        <Button compact mode="outlined" textColor="red" onPress={onDelete}>
          Delete
        </Button>
      </View>
    </View>
  )
}

const defaultFormState = {
  name: '',
  startDayOfWeekUTC: 1,
  startHoursUTC: 18,
  startMinutesUTC: 0,
  tableConfigs: [defaultTableConfig()] as TableConfigForm[]
}

export const AdminScheduledGamesScreen = () => {
  const router = useRouter()
  const schedules = useQuery(api.gameSchedules.queries.getPublicDailySchedules)
  const { createPublicDailySchedule, updateSchedule, cancelSchedule } =
    useConvexScheduledTournament()

  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingId, setEditingId] = useState<ScheduleDoc['_id'] | null>(null)
  const [form, setForm] = useState(defaultFormState)
  const [submitting, setSubmitting] = useState(false)

  const updateTableConfig = useCallback(
    (index: number, patch: Partial<TableConfigForm>) => {
      setForm((f) => ({
        ...f,
        tableConfigs: f.tableConfigs.map((tc, i) =>
          i === index ? { ...tc, ...patch } : tc
        )
      }))
    },
    []
  )

  const addTableConfig = useCallback(() => {
    setForm((f) => ({
      ...f,
      tableConfigs: [...f.tableConfigs, { ...defaultTableConfig(), _key: nextKey() }]
    }))
  }, [])

  const removeTableConfig = useCallback((index: number) => {
    setForm((f) => ({
      ...f,
      tableConfigs: f.tableConfigs.filter((_, i) => i !== index)
    }))
  }, [])

  const tableConfigsForApi = useCallback(() => {
    return form.tableConfigs.map(({ _key, ...tc }) => tc)
  }, [form.tableConfigs])

  const handleCreate = debounce(async () => {
    if (form.tableConfigs.length === 0) {
      Alert.alert('Error', 'Add at least one table config')
      return
    }
    setSubmitting(true)
    try {
      await createPublicDailySchedule({
        name: form.name,
        scheduledStartTime: nextOccurrenceFromDayTime(
          form.startDayOfWeekUTC,
          form.startHoursUTC,
          form.startMinutesUTC
        ),
        repeatDaysOfWeekUTC: [form.startDayOfWeekUTC],
        tableConfigs: tableConfigsForApi()
      })
      setForm(defaultFormState)
      setShowCreateForm(false)
    } catch (e) {
      console.error(e)
      Alert.alert('Error', e instanceof Error ? e.message : 'Failed to create schedule')
    } finally {
      setSubmitting(false)
    }
  }, 300)

  const handleUpdate = debounce(async () => {
    if (!editingId) return
    if (form.tableConfigs.length === 0) {
      Alert.alert('Error', 'Keep at least one table config')
      return
    }
    setSubmitting(true)
    try {
      await updateSchedule({
        scheduleId: editingId,
        name: form.name,
        scheduledStartTime: nextOccurrenceFromDayTime(
          form.startDayOfWeekUTC,
          form.startHoursUTC,
          form.startMinutesUTC
        ),
        repeatDaysOfWeekUTC: [form.startDayOfWeekUTC],
        tableConfigs: tableConfigsForApi()
      })
      setEditingId(null)
      setForm(defaultFormState)
    } catch (e) {
      console.error(e)
      Alert.alert('Error', e instanceof Error ? e.message : 'Failed to update schedule')
    } finally {
      setSubmitting(false)
    }
  }, 300)

  const handleDelete = (schedule: ScheduleDoc) => {
    Alert.alert(
      'Delete schedule',
      `Cancel "${schedule.name}"? This will stop new games from being created for this slot.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              await cancelSchedule(schedule._id)
            } catch (e) {
              console.error(e)
              Alert.alert('Error', e instanceof Error ? e.message : 'Failed to delete')
            }
          }
        }
      ]
    )
  }

  const startEdit = (schedule: ScheduleDoc) => {
    const configs = schedule.tableConfigs
    const { hours, minutes } = scheduleTimeOfDayUTC(schedule)
    setEditingId(schedule._id)
    setForm({
      name: schedule.name,
      startDayOfWeekUTC: scheduleDayOfWeekUTC(schedule) ?? 0,
      startHoursUTC: hours,
      startMinutesUTC: minutes,
      tableConfigs:
        configs && configs.length > 0
          ? configs.map((tc) => ({
              name: tc.name,
              difficulty: tc.difficulty,
              numberOfTables: tc.numberOfTables,
              blindTimer: tc.blindTimer,
              startingChips: tc.startingChips,
              turnDuration: tc.turnDuration,
              bigBlind: tc.bigBlind,
              smallBlind: tc.smallBlind,
              maxPlayers: tc.maxPlayers,
              minPlayers: tc.minPlayers,
              _key: nextKey()
            }))
          : [defaultTableConfig()]
    })
  }

  const cancelForm = () => {
    setShowCreateForm(false)
    setEditingId(null)
    setForm(defaultFormState)
  }

  const renderTableConfigForm = (tc: TableConfigForm, index: number) => (
    <View key={tc._key ?? index} style={styles.tableConfigBlock}>
      <View style={styles.tableConfigHeader}>
        <DS.Text variant="Body Small/Medium">Table config {index + 1}</DS.Text>
        <Button
          compact
          mode="text"
          textColor="red"
          onPress={() => removeTableConfig(index)}
          disabled={form.tableConfigs.length <= 1}
        >
          Remove
        </Button>
      </View>
      <TextInput
        style={styles.input}
        placeholder="Config name (e.g. Beginner, Main)"
        value={tc.name}
        onChangeText={(t) => updateTableConfig(index, { name: t })}
      />
      <DS.Text variant="Caption/Regular">Difficulty</DS.Text>
      <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap' }}>
        {DIFFICULTY_OPTIONS.map((d) => (
          <Button
            key={d}
            compact
            mode={tc.difficulty === d ? 'contained' : 'outlined'}
            onPress={() => updateTableConfig(index, { difficulty: d })}
          >
            {d}
          </Button>
        ))}
      </View>
      <DS.Text variant="Caption/Regular">Number of tables</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        value={String(tc.numberOfTables)}
        onChangeText={(t) =>
          updateTableConfig(index, { numberOfTables: Math.max(1, parseInt(t, 10) || 1) })
        }
      />
      <DS.Text variant="Caption/Regular">Blind timer (min)</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        value={String(tc.blindTimer)}
        onChangeText={(t) =>
          updateTableConfig(index, { blindTimer: Math.max(1, parseInt(t, 10) || 2) })
        }
      />
      <DS.Text variant="Caption/Regular">Starting chips</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        value={String(tc.startingChips)}
        onChangeText={(t) =>
          updateTableConfig(index, { startingChips: parseInt(t, 10) || 10000 })
        }
      />
      <DS.Text variant="Caption/Regular">Turn duration (sec)</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        value={String(tc.turnDuration)}
        onChangeText={(t) =>
          updateTableConfig(index, { turnDuration: Math.max(10, parseInt(t, 10) || 30) })
        }
      />
      <DS.Text variant="Caption/Regular">Big blind (optional)</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        placeholder="e.g. 200"
        value={tc.bigBlind != null ? String(tc.bigBlind) : ''}
        onChangeText={(t) =>
          updateTableConfig(index, {
            bigBlind: t === '' ? undefined : parseInt(t, 10) || 0
          })
        }
      />
      <DS.Text variant="Caption/Regular">Small blind (optional)</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        placeholder="e.g. 100"
        value={tc.smallBlind != null ? String(tc.smallBlind) : ''}
        onChangeText={(t) =>
          updateTableConfig(index, {
            smallBlind: t === '' ? undefined : parseInt(t, 10) || 0
          })
        }
      />
      <DS.Text variant="Caption/Regular">Max players (optional)</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        placeholder="e.g. 9"
        value={tc.maxPlayers != null ? String(tc.maxPlayers) : ''}
        onChangeText={(t) =>
          updateTableConfig(index, {
            maxPlayers: t === '' ? undefined : parseInt(t, 10) || 9
          })
        }
      />
      <DS.Text variant="Caption/Regular">Min players (optional)</DS.Text>
      <TextInput
        style={styles.input}
        keyboardType="number-pad"
        placeholder="e.g. 2"
        value={tc.minPlayers != null ? String(tc.minPlayers) : ''}
        onChangeText={(t) =>
          updateTableConfig(index, {
            minPlayers: t === '' ? undefined : parseInt(t, 10) || 2
          })
        }
      />
    </View>
  )

  return (
    <ImageBackground resizeMode="cover" source={image} style={styles.screen}>
      <SafeAreaView style={styles.screen}>
        <View style={styles.header}>
          <View style={styles.titleRow}>
            <MaterialIcons name="schedule" size={24} color="black" />
            <DS.Text variant="Heading 3/Regular">Community Scheduled Games</DS.Text>
          </View>
          <Pressable onPress={() => router.back()} hitSlop={10}>
            <AntDesign name="close" size={24} color="black" />
          </Pressable>
        </View>

        <KeyboardAvoidingView
          style={{ flex: 1 }}
          behavior={Platform.OS === 'ios' ? 'padding' : undefined}
          keyboardVerticalOffset={80}
        >
          <ScrollView contentContainerStyle={styles.content}>
            <Button
              icon="plus"
              mode="contained"
              onPress={() => {
                setShowCreateForm(true)
                setEditingId(null)
                setForm(defaultFormState)
              }}
            >
              Create community schedule
            </Button>

            {(showCreateForm || editingId) && (
              <View style={styles.form}>
                <DS.Text variant="Heading 4/Medium">
                  {editingId ? 'Edit schedule' : 'New schedule'}
                </DS.Text>

                <DS.Text variant="Body Small/Medium">Schedule name</DS.Text>
                <TextInput
                  style={styles.input}
                  placeholder="e.g. MONDAY 5PM PT"
                  value={form.name}
                  onChangeText={(t) => setForm((f) => ({ ...f, name: t }))}
                />

                <DS.Text variant="Body Small/Medium">Day (0=Sun … 6=Sat)</DS.Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(form.startDayOfWeekUTC)}
                  onChangeText={(t) =>
                    setForm((f) => ({
                      ...f,
                      startDayOfWeekUTC: Math.min(6, Math.max(0, parseInt(t, 10) || 0))
                    }))
                  }
                />
                <DS.Text variant="Caption/Regular">Hour UTC (0–23)</DS.Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(form.startHoursUTC)}
                  onChangeText={(t) =>
                    setForm((f) => ({
                      ...f,
                      startHoursUTC: Math.min(23, Math.max(0, parseInt(t, 10) || 0))
                    }))
                  }
                />
                <DS.Text variant="Caption/Regular">Minute UTC (0–59)</DS.Text>
                <TextInput
                  style={styles.input}
                  keyboardType="number-pad"
                  value={String(form.startMinutesUTC)}
                  onChangeText={(t) =>
                    setForm((f) => ({
                      ...f,
                      startMinutesUTC: Math.min(59, Math.max(0, parseInt(t, 10) || 0))
                    }))
                  }
                />

                <DS.Text variant="Body Small/Medium" style={styles.formSectionTitle}>
                  Table configs
                </DS.Text>
                {form.tableConfigs.map((tc, i) => renderTableConfigForm(tc, i))}
                <Button mode="outlined" onPress={addTableConfig} icon="plus">
                  Add table config
                </Button>

                <View style={styles.cardActions}>
                  <Button
                    mode="contained"
                    onPress={editingId ? handleUpdate : handleCreate}
                    disabled={submitting || !form.name}
                  >
                    {editingId ? 'Update' : 'Create'}
                  </Button>
                  <Button mode="outlined" onPress={cancelForm}>
                    Cancel
                  </Button>
                </View>
              </View>
            )}

            {schedules?.map((schedule) => (
              <ScheduleCard
                key={schedule._id}
                schedule={schedule}
                onEdit={() => startEdit(schedule)}
                onDelete={() => handleDelete(schedule)}
              />
            ))}
          </ScrollView>
        </KeyboardAvoidingView>
      </SafeAreaView>
    </ImageBackground>
  )
}
